import React, { useEffect, useState } from "react";
import { api, ZONES, DISTANCES } from "@/lib/handball";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

/**
 * Filter panel below the stats view.
 * Supports multi-select for zones + distances, single-select for shooter.
 * "Inkludera allt" resets the given category.
 */
export default function StatsFilters({
  goalkeeperId,
  sessionId,
  dateFrom,
  dateTo,
  value,
  onChange,
}) {
  const [shooters, setShooters] = useState([]);

  useEffect(() => {
    // Fetch distinct shooter numbers appearing in the current scope's shots
    (async () => {
      const params = {};
      if (goalkeeperId) params.goalkeeper_id = goalkeeperId;
      if (sessionId) params.session_id = sessionId;
      try {
        const { data } = await api.get("/shots", { params });
        const nums = Array.from(
          new Set(data.map((s) => s.shooter).filter((n) => n != null))
        ).sort((a, b) => a - b);
        setShooters(nums);
      } catch {
        setShooters([]);
      }
    })();
  }, [goalkeeperId, sessionId, dateFrom, dateTo]);

  const toggleZone = (z) => {
    const next = value.zones.includes(z)
      ? value.zones.filter((x) => x !== z)
      : [...value.zones, z];
    onChange({ ...value, zones: next });
  };
  const toggleDistance = (d) => {
    const next = value.distances.includes(d)
      ? value.distances.filter((x) => x !== d)
      : [...value.distances, d];
    onChange({ ...value, distances: next });
  };
  const clearZones = () => onChange({ ...value, zones: [] });
  const clearDistances = () => onChange({ ...value, distances: [] });
  const setShooter = (s) =>
    onChange({ ...value, shooter: s === "__all__" ? null : Number(s) });
  const setContact = (c) => onChange({ ...value, contact: c });
  const setLob = (c) => onChange({ ...value, lob: c });
  const anyFilter =
    value.zones.length > 0 || value.distances.length > 0 || value.shooter != null || value.contact != null || value.lob != null;

  return (
    <div className="card-tactical p-5" data-testid="stats-filters">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="section-hdr">
          <span className="marker" />
          Filter
        </div>
        {anyFilter && (
          <Button
            data-testid="filter-clear-all"
            onClick={() =>
              onChange({ zones: [], distances: [], shooter: null, contact: null, lob: null })
            }
            variant="outline"
            className="h-8 rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white text-xs"
          >
            Rensa alla
          </Button>
        )}
      </div>

      {/* Zones */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
            Zon (välj en eller flera)
          </div>
          <button
            type="button"
            data-testid="filter-zone-all"
            onClick={clearZones}
            className="text-[10px] uppercase tracking-widest font-display text-zinc-400 hover:text-white"
          >
            Inkludera allt
          </button>
        </div>
        <div className="grid grid-cols-6 gap-2">
          {ZONES.map((z) => (
            <button
              key={z}
              data-testid={`filter-zone-${z}`}
              data-active={value.zones.includes(z)}
              onClick={() => toggleZone(z)}
              className="seg-btn"
            >
              {z}
            </button>
          ))}
        </div>
      </div>

      {/* Distances */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
            Avstånd (välj en eller flera)
          </div>
          <button
            type="button"
            data-testid="filter-distance-all"
            onClick={clearDistances}
            className="text-[10px] uppercase tracking-widest font-display text-zinc-400 hover:text-white"
          >
            Inkludera allt
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {DISTANCES.map((d) => (
            <button
              key={d.value}
              data-testid={`filter-distance-${d.value}`}
              data-active={value.distances.includes(d.value)}
              onClick={() => toggleDistance(d.value)}
              className="seg-btn"
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {/* Shooter (only relevant for a single session) */}
      {sessionId && (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
            Skytt
          </div>
          <Select
            value={value.shooter != null ? String(value.shooter) : "__all__"}
            onValueChange={setShooter}
          >
            <SelectTrigger
              data-testid="filter-shooter-select"
              className="h-11 bg-black border-[#3F3F46] text-white rounded-none"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111] border-[#27272A] text-white max-h-[280px]">
              <SelectItem value="__all__">Alla skyttar</SelectItem>
              {shooters.length === 0 && (
                <div className="px-3 py-2 text-zinc-500 text-xs">
                  Inga skyttar loggade
                </div>
              )}
              {shooters.map((n) => (
                <SelectItem
                  key={n}
                  value={String(n)}
                  data-testid={`filter-shooter-option-${n}`}
                >
                  Skytt #{n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Contact vid skott (Ja/Nej/Alla) */}
      <div className="space-y-2 mt-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
          Kontakt vid skott
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            data-testid="filter-contact-all"
            data-active={value.contact == null}
            onClick={() => setContact(null)}
            className="seg-btn"
          >
            Alla
          </button>
          <button
            type="button"
            data-testid="filter-contact-yes"
            data-active={value.contact === true}
            onClick={() => setContact(true)}
            className="seg-btn"
          >
            Ja
          </button>
          <button
            type="button"
            data-testid="filter-contact-no"
            data-active={value.contact === false}
            onClick={() => setContact(false)}
            className="seg-btn"
          >
            Nej
          </button>
        </div>
      </div>

      {/* Lobb (Ja/Nej/Alla) */}
      <div className="space-y-2 mt-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
          Lobb
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            data-testid="filter-lob-all"
            data-active={value.lob == null}
            onClick={() => setLob(null)}
            className="seg-btn"
          >
            Alla
          </button>
          <button
            type="button"
            data-testid="filter-lob-yes"
            data-active={value.lob === true}
            onClick={() => setLob(true)}
            className="seg-btn"
          >
            Ja
          </button>
          <button
            type="button"
            data-testid="filter-lob-no"
            data-active={value.lob === false}
            onClick={() => setLob(false)}
            className="seg-btn"
          >
            Nej
          </button>
        </div>
      </div>
    </div>
  );
}
